import type React from 'react';
import { type FC, useEffect, useState } from 'react';
import TableCell from '@mui/material/TableCell';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, Dialog, DialogActions, DialogContent, IconButton, Tooltip } from '@mui/material';
import styled from 'styled-components';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { BoxesController } from '@/engine/app/interfaces';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ViewsService } from '@/engine/app/Views.service.ts';

export const UpdatableUserConfirmationRequiredIcon: FC<{
  descriptor: string;
  controller: BoxesController;
}> = ({ descriptor, controller }) => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);

  const [isDefault, setIsDefault] = useState<boolean>(false);

  const loggerService = useInjection<ObservabilityLoggerService>(CORE_TYPES.ObservabilityLoggerService);
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);

  useEffect(() => {
    const setIsDefaultJob = async (): Promise<void> => {
      const isDefault = await controller.isDefaultAsync(descriptor, false);
      setIsDefault(isDefault);
    };
    let subscription: IDisposable | undefined = planningAlgorithm.addOnChangedCallbackFor(descriptor, () => {
      void setIsDefaultJob();
    });
    void setIsDefaultJob();

    return (): void => {
      subscription?.dispose();
      subscription = undefined;
    };
  }, [planningAlgorithm, descriptor, controller]);

  if (!isDefault) return <></>;

  const vm = viewsService.currentView?.getVmByDescriptor(descriptor);

  if (!vm) throw new Error('View model should be defined!');

  loggerService.dispatchUndefinedValueErrorThrowing(vm);

  return (
    <Tooltip title="Edit the point to confirm its location" placement="left">
      <ErrorIcon color="warning" fontSize="small" />
    </Tooltip>
  );
};

export const UpdatableResetIconButton: FC<{
  descriptor: string;
  controller: BoxesController;
  title?: string;
  attachmentNumber?: number;
  handleTooltipDisabled?: (isDisabled: boolean) => void;
}> = ({ descriptor, controller, title = 'Reset', attachmentNumber, handleTooltipDisabled }) => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);

  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const reset = (descriptor: string, attachmentNumber?: number): Promise<void> =>
    controller.resetToDefaultAsync(descriptor, attachmentNumber);

  useEffect(() => {
    const setIsDefaultJob = async (): Promise<void> => {
      const isDefault = await controller.isDefaultAsync(descriptor, true, attachmentNumber);
      setIsDefault(isDefault);
    };
    let subscription: IDisposable | undefined = planningAlgorithm.addOnChangedCallbackFor(descriptor, () => {
      void setIsDefaultJob();
    });
    void setIsDefaultJob();

    return (): void => {
      subscription?.dispose();
      subscription = undefined;
    };
  }, [attachmentNumber, planningAlgorithm, descriptor, controller]);

  useEffect(() => {
    if (isDefault) {
      handleClose();
    }
  }, [isDefault]);

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const onCancel = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.stopPropagation();
    handleClose();
  };

  const onReset = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
    event.stopPropagation();
    await reset(descriptor, attachmentNumber);
  };

  return (
    <>
      <StyledIconButtonWrapper
        $isVisible={!isDefault}
        onMouseEnter={(): void => {
          handleTooltipDisabled?.(false);
        }}
        onMouseLeave={(): void => {
          handleTooltipDisabled?.(true);
        }}
      >
        <Tooltip title={title} placement="left">
          <IconButton
            size="small"
            color="inherit"
            onClick={(event): void => {
              event.stopPropagation();
              setIsOpen(true);
            }}
          >
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </StyledIconButtonWrapper>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogContent>Are you sure you want to reset this measurement to the original location?</DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
            onClick={onReset}
            variant="contained"
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const StyledTableCell = styled(TableCell)`
  &.limited-width {
    padding: 0;
    width: 2.125rem;
  }
  &.cell-height {
    height: 2.375rem;
  }
`;

export const StyledIconButtonWrapper = styled.div<{ $isVisible: boolean }>`
  display: ${(props): string => (props.$isVisible ? 'block' : 'none')};
`;
